/* Smart Flow Services - AI maintenance bots.
   Three techs (hard hat, muted hi-vis vest, glowing checklist tablet, cyan
   visor) glide the aisles between every rack row, front and back. Legless,
   they hover like ghost drones with a soft bob. They follow a waypoint
   graph whose nodes/edges are validated against each rack's footprint, so
   they can never pass through a server - only between them. */

/** Glowing checklist screen for the clipboard - drawn once, shared. */
function makeChecklistTexture(THREE) {
  const c = document.createElement('canvas');
  c.width = 96; c.height = 128;
  const g = c.getContext('2d');
  g.fillStyle = '#0a1420'; g.fillRect(0, 0, 96, 128);
  g.fillStyle = '#66ccf0'; g.fillRect(8, 8, 80, 10); // header bar
  const rows = [[16, '#3ddc84'], [40, '#3ddc84'], [64, '#66ccf0'], [88, '#ff5f56']];
  for (const [y, col] of rows) {
    g.strokeStyle = col; g.lineWidth = 2;
    g.strokeRect(10, y, 10, 10);                       // checkbox
    if (col === '#3ddc84') {                           // checked tick
      g.beginPath(); g.moveTo(12, y + 5); g.lineTo(15, y + 8); g.lineTo(19, y + 2); g.stroke();
    }
    g.fillStyle = '#9fb6cc';
    g.fillRect(26, y + 2, 58 - (y % 3) * 8, 6);        // text line
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** One bot: dark body, muted hi-vis vest + dull stripes, grey hard hat,
    cyan visor slit, checklist tablet held out in front. Legless - it
    hovers like a ghost, bobbing gently while it glides. ~1.5 units tall. */
function makeBot(THREE, ACCENT, checklistTex) {
  const bot = new THREE.Group();
  const dark = new THREE.MeshStandardMaterial({ color: 0x1a2230, roughness: 0.7, metalness: 0.3 });
  // Dry, muted tones - the crew blends into the hall instead of popping.
  const vestMat = new THREE.MeshStandardMaterial({
    color: 0x5f6547, roughness: 0.9, emissive: 0x1e2114, emissiveIntensity: 0.5,
  });
  const stripeMat = new THREE.MeshStandardMaterial({ color: 0x7d857c, roughness: 0.55, metalness: 0.4 });
  const hatMat = new THREE.MeshStandardMaterial({ color: 0x8f9599, roughness: 0.5, metalness: 0.15 });

  // Torso tapers toward the floor - no legs, the hover carries it.
  const torso = new THREE.Mesh(new THREE.CapsuleGeometry(0.19, 0.5, 4, 10), dark);
  torso.position.y = 0.95;
  const vest = new THREE.Mesh(new THREE.CylinderGeometry(0.23, 0.25, 0.38, 12), vestMat);
  vest.position.y = 1.06;
  const stripe1 = new THREE.Mesh(new THREE.CylinderGeometry(0.245, 0.245, 0.04, 12), stripeMat);
  stripe1.position.y = 1.14;
  const stripe2 = new THREE.Mesh(new THREE.CylinderGeometry(0.255, 0.255, 0.04, 12), stripeMat);
  stripe2.position.y = 0.98;

  // Head + glowing visor + hard hat (dome + brim).
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.15, 12, 10), dark);
  head.position.y = 1.45;
  const visor = new THREE.Mesh(
    new THREE.PlaneGeometry(0.17, 0.045),
    new THREE.MeshBasicMaterial({ color: ACCENT, toneMapped: false, fog: false })
  );
  visor.position.set(0, 1.46, 0.145);
  const hat = new THREE.Mesh(new THREE.SphereGeometry(0.17, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2), hatMat);
  hat.position.y = 1.5;
  const brim = new THREE.Mesh(new THREE.CylinderGeometry(0.21, 0.21, 0.015, 12), hatMat);
  brim.position.y = 1.5;

  // Right arm - sways gently while gliding. Pivot at the shoulder.
  const armGeo = new THREE.CapsuleGeometry(0.05, 0.3, 4, 8);
  armGeo.translate(0, -0.15, 0);
  const armR = new THREE.Mesh(armGeo, dark);
  armR.position.set(0.26, 1.22, 0);

  // Left arm + clipboard held out in front - the inspection pose.
  const armL = new THREE.Mesh(armGeo.clone(), dark);
  armL.position.set(-0.26, 1.22, 0);
  armL.rotation.x = -0.9;
  const board = new THREE.Mesh(
    new THREE.BoxGeometry(0.24, 0.32, 0.02),
    new THREE.MeshStandardMaterial({ color: 0x2a3442, roughness: 0.6 })
  );
  const screen = new THREE.Mesh(
    new THREE.PlaneGeometry(0.2, 0.28),
    new THREE.MeshBasicMaterial({ map: checklistTex, toneMapped: false })
  );
  screen.position.z = 0.012;
  board.add(screen);
  board.position.set(-0.16, 1.02, 0.62);
  board.rotation.x = -0.3;

  bot.add(torso, vest, stripe1, stripe2, head, visor, hat, brim, armR, armL, board);
  bot.userData.limbs = { armR };
  return bot;
}

export function buildBots(THREE, ACCENT, obstacles, count = 3) {
  const group = new THREE.Group();
  const checklistTex = makeChecklistTexture(THREE);

  // --- Lane graph -----------------------------------------------------------
  // Candidate waypoints on a grid over the whole hall. Nodes inside a rack
  // footprint are dropped; edges that cross a footprint are dropped. What
  // survives is, by construction, a graph the bots can glide without ever
  // touching a server.
  const XS = [-19.5, -16.9, -14.3, -11.7, -9.1, -6.5, -3.9, -2.6, -1.3, 0, 1.3, 2.6, 3.9, 6.5, 9.1, 11.7, 14.3, 16.9, 19.5];
  const ZS = [2.4, -3.2, -6.0, -8.0, -11.2, -15.7, -19.7, -24.2, -28.2];
  const inside = (x, z) => obstacles.some((o) => Math.abs(x - o.x) < o.hx && Math.abs(z - o.z) < o.hz);

  const nodes = [];
  for (const z of ZS) for (const x of XS) if (!inside(x, z)) nodes.push({ x, z, edges: [] });

  const segClear = (a, b) => {
    const steps = Math.ceil(Math.hypot(b.x - a.x, b.z - a.z) / 0.2);
    for (let i = 1; i < steps; i++) {
      const x = a.x + ((b.x - a.x) * i) / steps;
      const z = a.z + ((b.z - a.z) * i) / steps;
      if (inside(x, z)) return false;
    }
    return true;
  };
  // Connect each node to its nearest surviving neighbor along its row/column.
  for (const a of nodes) {
    for (const b of nodes) {
      if (a === b) continue;
      const sameRow = a.z === b.z && Math.abs(a.x - b.x) <= 2.61;
      const sameCol = a.x === b.x && Math.abs(a.z - b.z) <= 4.9;
      if ((sameRow || sameCol) && segClear(a, b)) a.edges.push(b);
    }
  }

  const findPath = (from, to) => {
    const prev = new Map([[from, null]]);
    const queue = [from];
    while (queue.length) {
      const n = queue.shift();
      if (n === to) {
        const path = [];
        for (let p = to; p; p = prev.get(p)) path.unshift(p);
        return path;
      }
      for (const e of n.edges) if (!prev.has(e)) { prev.set(e, n); queue.push(e); }
    }
    return [from];
  };

  // --- Bots -----------------------------------------------------------------
  const bots = [];
  for (let i = 0; i < count; i++) {
    const mesh = makeBot(THREE, ACCENT, checklistTex);
    const start = nodes[Math.floor(Math.random() * nodes.length)];
    mesh.position.set(start.x, 0, start.z);
    group.add(mesh);
    bots.push({
      mesh, at: start, path: [], heading: Math.random() * Math.PI * 2,
      phase: Math.random() * 6, pause: 0, speed: 0.85 + Math.random() * 0.25,
    });
  }

  let lastT = 0;
  function update(t, reduced) {
    const dt = Math.min(t - lastT, 0.1);
    lastT = t;
    if (reduced || dt <= 0) return; // parked under prefers-reduced-motion

    for (const b of bots) {
      // Inspecting a rack: hover in place, face it, tablet up. Then move on.
      if (b.pause > 0) {
        b.pause -= dt;
        b.phase += dt * 1.3; // slow ghost bob while stationary
        settle(b, 0, dt);
        continue;
      }
      if (!b.path.length) {
        // New random patrol destination; sometimes stop to inspect first.
        if (Math.random() < 0.45) {
          b.pause = 2 + Math.random() * 2.5;
          faceNearestObstacle(b);
          continue;
        }
        let dest = b.at;
        while (dest === b.at) dest = nodes[Math.floor(Math.random() * nodes.length)];
        b.path = findPath(b.at, dest).slice(1);
        if (!b.path.length) { b.pause = 1; continue; } // unreachable node - try again shortly
      }
      const next = b.path[0];
      const dx = next.x - b.mesh.position.x;
      const dz = next.z - b.mesh.position.z;
      const dist = Math.hypot(dx, dz);
      if (dist < 0.08) {
        b.at = next;
        b.path.shift();
        continue;
      }
      const targetHeading = Math.atan2(dx, dz);
      let dh = targetHeading - b.heading;
      while (dh > Math.PI) dh -= Math.PI * 2;
      while (dh < -Math.PI) dh += Math.PI * 2;
      b.heading += dh * Math.min(1, dt * 6);
      const step = Math.min(dist, b.speed * dt);
      b.mesh.position.x += Math.sin(b.heading) * step;
      b.mesh.position.z += Math.cos(b.heading) * step;

      // Don't bump into the other techs.
      for (const o of bots) {
        if (o === b) continue;
        const sx = b.mesh.position.x - o.mesh.position.x;
        const sz = b.mesh.position.z - o.mesh.position.z;
        const sd = Math.hypot(sx, sz);
        if (sd > 0.001 && sd < 0.7) {
          b.mesh.position.x += (sx / sd) * (0.7 - sd) * 0.5;
          b.mesh.position.z += (sz / sd) * (0.7 - sd) * 0.5;
        }
      }
      b.phase += dt * 2.4 * b.speed;
      settle(b, 1, dt);
    }
  }

  // Hover settle. k=1 gliding, k=0 inspecting - either way it floats and
  // bobs gently like a ghost drone; no legs to swing. A slight forward
  // lean while gliding, plus a faint sideways sway.
  function settle(b, k, dt) {
    const { armR } = b.mesh.userData.limbs;
    const s = Math.sin(b.phase) * 0.5 * k;
    armR.rotation.x += (-s * 0.6 - armR.rotation.x) * Math.min(1, dt * 10);
    b.mesh.rotation.y = b.heading;
    b.mesh.rotation.x += (0.07 * k - b.mesh.rotation.x) * Math.min(1, dt * 4);
    b.mesh.rotation.z = Math.sin(b.phase * 0.7) * 0.03;
    // Float base off the floor + a soft up-down bob that keeps going
    // whether it's gliding or paused.
    const hover = 0.42 + Math.sin(b.phase * 0.9) * 0.09;
    b.mesh.position.y += (hover - b.mesh.position.y) * Math.min(1, dt * 4);
  }

  function faceNearestObstacle(b) {
    let best = null, bd = 1e9;
    for (const o of obstacles) {
      const d = Math.hypot(o.x - b.mesh.position.x, o.z - b.mesh.position.z);
      if (d < bd) { bd = d; best = o; }
    }
    if (best) b.heading = Math.atan2(best.x - b.mesh.position.x, best.z - b.mesh.position.z);
  }

  return { group, update };
}
